import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useSearchParams } from 'react-router-dom';
import QuestionCard from '../components/questions/QuestionCard';
import ShortAnswerQuestionCard from '../components/questions/ShortAnswerQuestionCard';
import { questionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import FreeTierLimitModal from '../components/modals/FreeTierLimitModal';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { isFreeTierLimitError } from '../utils/freeTier';
import {
  QuestionApiModel,
  QuestionTypeCode,
  ShortAnswerQuestionGroup,
  isShortAnswerQuestionGroup,
} from '../types/questions';
import { groupShortAnswerQuestions, isWrittenResponseType } from '../utils/questions';
import { AttemptResult } from '../types/practice';
import { getUserMessage } from '../utils/errors';
import { useActiveSubjectId } from '../subjects/subjectContextValue';
import { SUBJECT_AREAS, SUBJECT_AREA_DESCRIPTIONS } from '../subjects/areas';

type QuestionTypeSelection = 'MC' | 'SA' | 'ER' | 'BOTH';
type PracticeQuestion = QuestionApiModel | ShortAnswerQuestionGroup;


const questionTypeOptions: { label: string; value: QuestionTypeSelection; description?: string }[] = [
  { label: 'Multiple Choice Only', value: 'MC' },
  {
    label: 'Short Answer Only',
    value: 'SA',
    description:
      'In the actual exam, these questions are not multiple choice. Cracked At formats them as multiple choice for practice.',
  },
  {
    label: 'Extended Response Only',
    value: 'ER',
    description:
      'In the actual exam, these questions are not multiple choice. Cracked At formats them as multiple choice for practice.',
  },
  { label: 'Mix All Types', value: 'BOTH' },
];

const Practice: React.FC = () => {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedTopic = searchParams.get('topic');
  const [question, setQuestion] = useState<PracticeQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [answerStartTime, setAnswerStartTime] = useState<number>(Date.now());
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [questionTypeSelection, setQuestionTypeSelection] = useState<QuestionTypeSelection | null>(null);
  const [pendingQuestionTypeSelection, setPendingQuestionTypeSelection] = useState<QuestionTypeSelection>('BOTH');
  const activeSubjectId = useActiveSubjectId();
  const areaOptions = SUBJECT_AREAS[activeSubjectId];
  const areaDescriptions = SUBJECT_AREA_DESCRIPTIONS[activeSubjectId] ?? {};
  const [selectedAreas, setSelectedAreas] = useState<string[]>(() => (selectedTopic ? [selectedTopic] : []));
  const { subscriptionStatus, isLoading: isSubscriptionLoading, refresh: refreshSubscriptionStatus, setSubscriptionStatus } =
    useSubscriptionStatus();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const hasFetchedInitialQuestion = useRef(false);
  const lastAreaFilterRef = useRef<string>('');

  const practiceUsage = subscriptionStatus?.free_tier_usage;
  const selectedAreaFilterKey = useMemo(() => selectedAreas.join('|'), [selectedAreas]);

  const practiceLimitReached = useMemo(() => {
    if (!subscriptionStatus?.is_on_free_plan || !practiceUsage) {
      return false;
    }
    return practiceUsage.practice_questions_used >= practiceUsage.practice_questions_limit;
  }, [practiceUsage, subscriptionStatus?.is_on_free_plan]);

  const isSelectionPending = questionTypeSelection === null;
  const isPageLoading =
    (loading || isSubscriptionLoading) && !question && !error && !practiceLimitReached && !isSelectionPending;

  const practiceUsageLabel = practiceUsage
    ? `${practiceUsage.practice_questions_used}/${practiceUsage.practice_questions_limit} questions`
    : null;

  const practiceResetLabel = useMemo(() => {
    if (!practiceUsage) return null;
    try {
      const resetDate = new Date(practiceUsage.resets_at);
      return `Resets ${resetDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    } catch {
      return `Resets ${practiceUsage.resets_on}`;
    }
  }, [practiceUsage]);

  const getQuestionTypeParam = (selection: QuestionTypeSelection | null): QuestionTypeCode | undefined => {
    if (!selection || selection === 'BOTH') {
      return undefined;
    }
    return selection;
  };

  const resetForNewSelection = useCallback(() => {
    setQuestion(null);
    setAttemptResult(null);
    setError(null);
    hasFetchedInitialQuestion.current = false;
  }, []);

  const fetchRandomQuestion = useCallback(async () => {
    if (practiceLimitReached) {
      setShowLimitModal(true);
      setQuestion(null);
      setLoading(false);
      return;
    }

    if (!questionTypeSelection) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setAttemptResult(null);
      setQuestion(null);

      const params: Record<string, string | number | string[]> = {};
      if (selectedAreas.length > 0) {
        params.vce_area_of_study = selectedAreas;
      }
      const typeParam = getQuestionTypeParam(questionTypeSelection);
      if (typeParam) {
        params.question_type = typeParam;
      }

      const data: QuestionApiModel = await questionAPI.getRandomQuestion(params);

      if (isWrittenResponseType(data.question_type)) {
        const relatedQuestions = await questionAPI.getQuestions({
          year: data.year,
          exam_type: data.exam_type,
          question_number: data.question_number,
          question_type: data.question_type,
          limit: 20,
          skip: 0,
        });

        const questionList: QuestionApiModel[] = Array.isArray(relatedQuestions)
          ? (relatedQuestions as QuestionApiModel[])
          : [relatedQuestions as QuestionApiModel];

        const grouped = groupShortAnswerQuestions(questionList);
        const group =
          grouped.find(
            (item) =>
              item.year === data.year &&
              item.exam_type === data.exam_type &&
              item.question_number === data.question_number
          ) ?? grouped[0];

        if (!group) {
          throw new Error('Unable to load written response sub-questions.');
        }

        setQuestion(group);
      } else {
        setQuestion(data);
        setAnswerStartTime(Date.now());
      }

      await refreshSubscriptionStatus().catch(() => {});
    } catch (err: any) {
      if (isFreeTierLimitError(err)) {
        setShowLimitModal(true);
        setQuestion(null);
        await refreshSubscriptionStatus().catch(() => {});
      } else if (err?.response?.status === 404) {
        setQuestion(null);
        setError(
          selectedAreas.length
            ? 'No questions found for the selected area(s). Clear or change areas of study and try again.'
            : 'No questions found matching these filters. Try a different question type.',
        );
      } else {
        setError(getUserMessage(err, 'Unable to load a practice question. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  }, [practiceLimitReached, questionTypeSelection, refreshSubscriptionStatus, selectedAreas]);

  useEffect(() => {
    if (isSubscriptionLoading) {
      return;
    }

    const areaFilterChanged = lastAreaFilterRef.current !== selectedAreaFilterKey;
    if (!questionTypeSelection) {
      return;
    }

    if (!hasFetchedInitialQuestion.current || areaFilterChanged) {
      if (practiceLimitReached) {
        setShowLimitModal(true);
        setQuestion(null);
        setLoading(false);
        hasFetchedInitialQuestion.current = true;
        lastAreaFilterRef.current = selectedAreaFilterKey;
        return;
      }

      fetchRandomQuestion();
      hasFetchedInitialQuestion.current = true;
      lastAreaFilterRef.current = selectedAreaFilterKey;
    }
  }, [fetchRandomQuestion, isSubscriptionLoading, practiceLimitReached, questionTypeSelection, selectedAreaFilterKey]);

  const submitAnswer = useCallback(
    async ({
      questionId,
      selectedAnswer,
      timeSpentSeconds,
    }: {
      questionId: string;
      selectedAnswer: string;
      timeSpentSeconds?: number;
    }) => {
      if (practiceLimitReached) {
        setShowLimitModal(true);
        throw new Error('Practice limit reached');
      }

      try {
        const result = await questionAPI.submitAnswer({
          question_id: questionId,
          selected_answer: selectedAnswer,
          time_spent_seconds: timeSpentSeconds,
          game_mode: 'practice',
        });

        if (result.free_tier_usage) {
          setSubscriptionStatus((prev) =>
            prev
              ? {
                  ...prev,
                  free_tier_usage: result.free_tier_usage,
                }
              : prev
          );
        }

        if (result.question_progress) {
          setQuestion((prev) => {
            if (!prev) {
              return prev;
            }

            if (isShortAnswerQuestionGroup(prev)) {
              return {
                ...prev,
                progress: result.question_progress ?? prev.progress,
                sub_questions: prev.sub_questions.map((sub) =>
                  sub.id === questionId
                    ? {
                        ...sub,
                        progress: result.question_progress ?? sub.progress,
                      }
                    : sub
                ),
              };
            }

            if (prev.id === questionId) {
              return {
                ...prev,
                progress: result.question_progress ?? prev.progress,
              };
            }

            return prev;
          });
        }

        await refreshUser();
        await refreshSubscriptionStatus().catch(() => {});
        return result;
      } catch (err: any) {
        if (isFreeTierLimitError(err)) {
          setShowLimitModal(true);
          setQuestion(null);
          await refreshSubscriptionStatus().catch(() => {});
          throw new Error('Daily practice limit reached.');
        }

        const message = getUserMessage(err, 'Failed to submit answer. Please try again.');
        throw new Error(message);
      }
    },
    [practiceLimitReached, refreshSubscriptionStatus, refreshUser, setSubscriptionStatus]
  );

  const handleAnswer = async (selectedAnswerValue: string, timeSpentSeconds?: number) => {
    if (!question || submittingAnswer) return;
    if (isShortAnswerQuestionGroup(question)) return;
    if (practiceLimitReached) {
      setShowLimitModal(true);
      return;
    }

    setSubmittingAnswer(true);
    try {
      const resolvedTimeSpentSeconds =
        typeof timeSpentSeconds === 'number'
          ? timeSpentSeconds
          : Math.floor((Date.now() - answerStartTime) / 1000);

      const result = await submitAnswer({
        questionId: question.id,
        selectedAnswer: selectedAnswerValue,
        timeSpentSeconds: resolvedTimeSpentSeconds,
      });

      setAttemptResult(result);
    } catch (err: any) {
      console.error('Failed to submit answer:', err);
      setError(getUserMessage(err, 'Failed to submit answer. Please try again.'));
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleShortAnswerSubmission = useCallback(
    async ({
      questionId,
      selectedAnswer,
      timeSpentSeconds,
    }: {
      questionId: string;
      selectedAnswer: string;
      timeSpentSeconds?: number;
    }) => {
      return submitAnswer({
        questionId,
        selectedAnswer,
        timeSpentSeconds,
      });
    },
    [submitAnswer]
  );

  const handleNewQuestion = () => {
    if (practiceLimitReached) {
      setShowLimitModal(true);
      return;
    }
    fetchRandomQuestion();
  };

  const handleNextQuestion = () => {
    if (practiceLimitReached) {
      setShowLimitModal(true);
      return;
    }
    fetchRandomQuestion();
  };

  const handleQuestionTypeSelect = (selection: QuestionTypeSelection) => {
    resetForNewSelection();
    setQuestionTypeSelection(selection);
  };

  const handleResetQuestionType = () => {
    setQuestionTypeSelection(null);
    resetForNewSelection();
  };

  const handlePendingQuestionTypeSelect = (selection: QuestionTypeSelection) => {
    setPendingQuestionTypeSelection(selection);
  };

  const toggleAreaSelection = (area: string) => {
    setSelectedAreas((current) =>
      current.includes(area) ? current.filter((value) => value !== area) : [...current, area]
    );
  };

  const handleStartPractice = () => {
    handleQuestionTypeSelect(pendingQuestionTypeSelection);
  };

  const handleClearAreaFilter = () => {
    setSelectedAreas([]);
    resetForNewSelection();
  };

  if (isSelectionPending) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full p-8 border border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-2">
            Choose Your Practice Style
          </h1>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
            Select the question types you'd like to include in this practice session.
          </p>

          <div className="space-y-6">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Choose question types</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {questionTypeOptions.map((option) => {
                  const isSelected = pendingQuestionTypeSelection === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => handlePendingQuestionTypeSelect(option.value)}
                      className={`rounded-xl border p-6 text-left hover:border-subject-400 hover:shadow-lg transition-all ${
                        isSelected
                          ? 'border-subject-500 bg-subject-50 dark:border-subject-600 dark:bg-subject-900/30'
                          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                      }`}
                    >
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {option.label}
                      </h3>
                      {option.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{option.description}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Choose areas of study</h2>
              <div className={`grid gap-4 ${areaOptions.length > 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-3'}`}>
                {areaOptions.map((area) => {
                  const isSelected = selectedAreas.includes(area);
                  const description = areaDescriptions[area];
                  return (
                    <button
                      key={area}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => toggleAreaSelection(area)}
                      className={`rounded-xl border p-5 text-left hover:border-subject-400 hover:shadow-lg transition-all ${
                        isSelected
                          ? 'border-subject-500 bg-subject-50 dark:border-subject-600 dark:bg-subject-900/30'
                          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                      }`}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{area}</h3>
                      {description ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleStartPractice}
                className="px-6 py-3 rounded-lg bg-subject-600 text-white font-medium hover:bg-subject-500 transition-colors"
              >
                Start Practice
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-subject-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading question...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Error Loading Question
            </h2>
            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                fetchRandomQuestion();
              }}
              className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {subscriptionStatus?.is_on_free_plan && practiceUsage && (
          <div
            className={`mb-6 rounded-lg border p-4 ${
              practiceLimitReached
                ? 'border-subject-300 bg-subject-50 text-subject-800 dark:border-subject-800/40 dark:bg-subject-900/20 dark:text-subject-200'
                : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/40 dark:bg-blue-900/20 dark:text-blue-200'
            }`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold">Free Plan Daily Practice</p>
                <p className="text-sm">
                  {practiceLimitReached
                    ? 'You have reached today’s free practice limit. Come back tomorrow or upgrade for unlimited practice.'
                    : 'Keep going! Upgrade anytime for unlimited practice sessions.'}
                </p>
              </div>
              <div className="text-sm sm:text-right">
                {practiceUsageLabel && <p className="font-semibold">{practiceUsageLabel}</p>}
                {practiceResetLabel && <p className="text-xs opacity-80">{practiceResetLabel}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-subject-600 dark:hover:text-subject-400 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Dashboard
            </Link>
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Practice Mode</h1>
              {selectedAreas.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {selectedAreas.map((area) => (
                    <span key={area} className="px-3 py-1 bg-subject-100 dark:bg-subject-900 text-subject-800 dark:text-subject-200 rounded-full text-sm font-medium">
                      {area}
                    </span>
                  ))}
                  <span className="text-sm text-gray-600 dark:text-gray-400">Area Filter Active</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleResetQuestionType}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Change Question Type
            </button>
            {selectedAreas.length > 0 && (
              <button
                type="button"
                onClick={handleClearAreaFilter}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Clear Filter
              </button>
            )}
            <button
              onClick={handleNewQuestion}
              disabled={practiceLimitReached || loading}
              className={`px-4 py-2 rounded-lg transition-colors ${
                practiceLimitReached || loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-subject-600 text-white hover:bg-subject-500'
              }`}
            >
              New Question
            </button>
          </div>
        </div>

        {question && (
          <div className="mb-8">
            {!isShortAnswerQuestionGroup(question) ? (
              <QuestionCard
                question={question}
                onAnswer={handleAnswer}
                onNextQuestion={handleNextQuestion}
                isSubmitting={submittingAnswer}
                attemptResult={attemptResult}
                workedSolution={question.worked_solution ?? undefined}
                showSimilarQuestion
                canGenerateSimilar={!!subscriptionStatus?.has_active_subscription}
                showTimer
              />
            ) : (
              <ShortAnswerQuestionCard
                question={question}
                onSubmitAnswer={({ questionId, selectedAnswer, timeSpentSeconds }) =>
                  handleShortAnswerSubmission({ questionId, selectedAnswer, timeSpentSeconds })
                }
                onNextQuestion={handleNextQuestion}
                practiceLimitReached={practiceLimitReached}
                showSimilarQuestion
                canGenerateSimilar={!!subscriptionStatus?.has_active_subscription}
                showTimer
              />
            )}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Practice Tips</h3>
          <ul className="space-y-2 text-gray-600 dark:text-gray-300">
            <li>• Take your time to read each question carefully</li>
            <li>• Mathematical expressions are rendered with proper formatting</li>
            <li>• Your answer is recorded when you click Submit</li>
            <li>• Click "New Question" to practice with different problems</li>
          </ul>
        </div>

        <FreeTierLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature="practice"
          usage={subscriptionStatus?.free_tier_usage}
        />
      </div>
    </div>
  );
};

export default Practice;
